// Assessment like in a real check-out: safety first, then efficiency and communication. Events carry a German
// explanation for the debriefing. A critical event (collision, near miss, landing on an occupied runway)
// ends the session – "you have been relieved from your position".
import type { World, Request } from './world';
import type { Aircraft } from './aircraft';
import type { Pilot } from './pilot';
import type { Cmd } from './commands';

export type Severity = 'info' | 'minor' | 'major' | 'critical';
export interface SimEvent {
  id: number;
  t: number;
  kind: 'safety' | 'efficiency' | 'comms' | 'info';
  sev: Severity;
  code: string;
  text: string;
  cs?: string;
  /** Positive: bonus, negative: deduction (defaults by severity). */
  points: number;
}

const DEDUCT: Record<Severity, number> = { info: 0, minor: -3, major: -12, critical: -45 };
let evId = 1;

export interface ScoreSummary {
  safety: number;
  efficiency: number;
  comms: number;
  total: number;
  relieved: boolean;
  relievedReason: string | null;
  movements: number;
  playerMovements: number;
  readbackErrors: number;
  readbackCaught: number;
  avgResponse: number;
  goArounds: number;
  counts: Record<Severity, number>;
}

export class Score {
  private safety = 100;
  private efficiency = 100;
  private comms = 100;
  relieved = false;
  relievedReason: string | null = null;
  movements = 0;
  playerMovements = 0;
  readbackErrors = 0;
  readbackCaught = 0;
  goArounds = 0;
  private responses: number[] = [];
  private dedupe = new Map<string, number>();
  counts: Record<Severity, number> = { info: 0, minor: 0, major: 0, critical: 0 };

  constructor(private readonly w: World) {}

  /** Records an event; identical events for the same aircraft within 60 s are merged. Returns null if merged. */
  add(e: Omit<SimEvent, 'id' | 'points'> & { points?: number }): SimEvent | null {
    const key = `${e.code}|${e.cs ?? ''}`;
    const last = this.dedupe.get(key);
    if (last !== undefined && e.t - last < 60) return null;
    this.dedupe.set(key, e.t);
    const points = e.points ?? DEDUCT[e.sev];
    const ev: SimEvent = { ...e, id: evId++, points };
    this.counts[e.sev]++;
    const target = e.kind === 'safety' ? 'safety' : e.kind === 'efficiency' ? 'efficiency' : e.kind === 'comms' ? 'comms' : null;
    if (target) this[target] = Math.max(0, Math.min(100, this[target] + points));
    if (e.sev === 'critical' && e.kind === 'safety') { this.relieved = true; this.relievedReason = e.text; }
    return ev;
  }

  movementStarted(_ac: Aircraft): void { /* counted on completion */ }
  movementCompleted(ac: Aircraft, _p: Pilot): void {
    this.movements++;
    if (this.touchedByPlayer.has(ac.cs)) this.playerMovements++;
  }
  landed(_p: Pilot): void { /* landings are counted when parked */ }
  private touchedByPlayer = new Set<string>();
  playerCommand(ac: Aircraft, _cmds: Cmd[]): void { this.touchedByPlayer.add(ac.cs); }
  readbackError(_cs: string, _right: Cmd, _wrong: Cmd): void { this.readbackErrors++; }
  goAround(_p: Pilot, _own: boolean): void { this.goArounds++; }
  requestAnswered(r: Request): void {
    if (!this.w.playerUnits().includes(r.unit)) return;
    const lat = this.w.t - r.t;
    this.responses.push(lat);
    if (lat > 120) this.add({ t: this.w.t, kind: 'efficiency', sev: 'minor', code: 'SLOW_RESPONSE', text: `${r.cs} hat ${Math.round(lat)} s auf eine Antwort gewartet.`, cs: r.cs });
  }
  caught(): void { this.readbackCaught++; }

  summary(): ScoreSummary {
    const avg = this.responses.length ? this.responses.reduce((a, b) => a + b, 0) / this.responses.length : 0;
    // slow responses lower efficiency a little even without discrete events
    const eff = Math.max(0, this.efficiency - Math.max(0, avg - 30) * 0.15);
    const total = Math.round(this.safety * 0.5 + eff * 0.25 + this.comms * 0.25);
    return {
      safety: Math.round(this.safety), efficiency: Math.round(eff), comms: Math.round(this.comms), total: this.relieved ? Math.min(total, 30) : total,
      relieved: this.relieved, relievedReason: this.relievedReason, movements: this.movements, playerMovements: this.playerMovements,
      readbackErrors: this.readbackErrors, readbackCaught: this.readbackCaught, avgResponse: Math.round(avg), goArounds: this.goArounds, counts: { ...this.counts },
    };
  }
}
